import { useEffect } from 'react';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useUser } from '@/contexts/UserContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { OnboardingWizard } from './OnboardingWizard';

interface OnboardingGateProps {
  setCurrentPage: (page: string) => void;
  setSettingsTab?: (tab: string) => void;
}

/**
 * OnboardingGate — decides whether to render the wizard overlay.
 *
 * Rules:
 * - Renders null while user context is loading (bootstrapState === 'loading')
 * - Renders null while the server-side completion check is still in flight
 *   (prevents a false-positive auto-show before the server has been consulted)
 * - Only shows for Administrator users
 *
 * Auto-show behaviour:
 * - ONLY auto-shows when serverStatus === 'notStarted' (no admin has ever
 *   opened this wizard for the current company/instance).
 * - If another admin has already started onboarding (serverStatus === 'inProgress'),
 *   the wizard does NOT auto-show — the admin can resume via the sidebar button.
 * - If onboarding is completed (serverStatus === 'completed'), the wizard
 *   never shows automatically or manually until a new company is provisioned.
 * - In demo mode the wizard NEVER auto-shows on load; it remains accessible
 *   via the sidebar "Start Onboarding" button.
 *
 * This ensures onboarding runs once per company, not once per administrator.
 */
export function OnboardingGate({ setCurrentPage, setSettingsTab }: OnboardingGateProps) {
  const { bootstrapState, user } = useUser();
  const {
    serverStatus,
    hasDismissed,
    isWizardOpen,
    isCheckingServer,
    openWizard,
  } = useOnboarding();
  const { isDemo } = useDataMode();

  const isAdministrator = user.workspaceRole === 'Administrator';
  const isDemoMode = isDemo('calendar');

  // Auto-show logic: only open for the very first admin on a fresh setup.
  // - Wait for both user context and server status to be ready.
  // - Only show if no admin has started or completed the wizard yet.
  // - Once dismissed for this session, don't re-show (user must use sidebar).
  // - In demo mode, never auto-show — user can trigger it from the sidebar.
  useEffect(() => {
    if (isDemoMode) return;                 // demo mode — suppress auto-show
    if (bootstrapState !== 'ready') return;
    if (isCheckingServer) return;           // wait for server sync before deciding
    if (!isAdministrator) return;
    if (serverStatus !== 'notStarted') return; // another admin started/completed → no auto-show
    if (hasDismissed) return;               // dismissed this session — respect that choice
    if (isWizardOpen) return;

    openWizard();
  }, [
    isDemoMode,
    bootstrapState,
    isCheckingServer,
    isAdministrator,
    serverStatus,
    hasDismissed,
    // isWizardOpen intentionally excluded — we only want to trigger once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // Don't render anything while still loading or not an admin
  if (bootstrapState === 'loading') return null;
  if (isCheckingServer) return null;
  if (!isAdministrator) return null;
  if (!isWizardOpen) return null;

  return (
    <OnboardingWizard
      setCurrentPage={setCurrentPage}
      setSettingsTab={setSettingsTab}
    />
  );
}
